import { Amplify } from 'aws-amplify';
import { signOut } from 'aws-amplify/auth';
import aws_exports from '../aws-exports';
import axios from 'axios';
import { UserPreferences, UserRoles } from '../commontypes/types';
import { getCurrentUserAndGroups, getJWTToken } from '../pages/DefaultPages/common/UserUtils';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RestStatus } from '../commontypes/common';
import { RootState } from '.';
import { AdditionalContact, CognitoUser, County, Municipality, NewAppeal, SolicitorContact, UserConfiguration, UserConfigurationResponse, UserContactDefault } from '../commontypes/types';
import { CountyDefinitions } from '../pages/DefaultPages/common/CountyDefinitions';
import { downloadStateAppealsForCounty } from './modIV';
Amplify.configure(aws_exports);

axios.interceptors.request.use(
  async (request) => {
    const token = await getJWTToken();
    if (token) {
      request.headers.Authorization = token;
    } else {
      await signOut();
    }
    return request;
  },
  null,
  { runWhen: (config) => !!config.url && config.url.startsWith(process.env.REACT_APP_API_URL!) }
);
export type MenuLauncher = {
  title: string;
  key: string;
  url: string;
  icon?: string;
  disabled?: boolean;
}

export type MenuFolder = {
  title: string;
  key: string;
  icon?: string;
  disabled?: boolean;
  children: MenuLauncher[];
}

export type MenuEntry = MenuLauncher | MenuFolder;


interface UserState {
  cognitoUser?: CognitoUser;
  user: UserConfiguration;
  userGroups: string[];
  aatLoaded: boolean;
  aatLoading?: boolean;
  aatError?: string;
  county?: County;
  countyIsInactive: boolean;
  municipalities?: Municipality[];
  isAdmin: boolean;
  isCountyAdmin: boolean;
  isAssessor: boolean;
  isSolicitor: boolean;
  isCountyClerk: boolean;
  isAuthorizedAttorney: boolean;
  isCountyCommissioner: boolean;
}

interface LayoutState {
  isMenuTop: boolean;
  menuMobileOpened: boolean;
  menuCollapsed: boolean;
  menuShadow: boolean;
  themeLight: boolean;
  squaredBorders: boolean;
  borderLess: boolean;
  fixedWidth: boolean;
  settingsOpened: boolean;
  refreshNeeded: boolean;
}

interface AppState {
  from: string;
  isUpdatingContent: boolean;
  isLoading: boolean;
  dialogForms: Record<string, any>;
  contentBuffer: {
    pathName: string | null;
    content: any | null;
  };
  menuData: MenuEntry[];
  layoutState: LayoutState;
  userState: UserState;
  userPreferences?: UserPreferences;
  userPreferencesRestStatus: {
    status: RestStatus;
    source?: string;
    requestId?: string;
    error?: string
  };
  userActivityRestStatus: {
    status: RestStatus;
    requestId?: string;
    error?: string;
  };
  reminderRestStatus?: {
    status: RestStatus;
    error?: string;
  };
  numberOfUsernamesFound?: number;
  networkError?: string;
}

const initialState: AppState = {
  from: '',
  isUpdatingContent: false,
  isLoading: false,
  dialogForms: {},
  contentBuffer: {
    pathName: null,
    content: null,
  },
  menuData: [],
  layoutState: {
    isMenuTop: false,
    menuMobileOpened: false,
    menuCollapsed: false,
    menuShadow: true,
    themeLight: true,
    squaredBorders: false,
    borderLess: true,
    fixedWidth: false,
    settingsOpened: false,
    refreshNeeded: false,
  },
  userState: {
    user: {},
    userGroups: [],
    aatLoaded: false,
    countyIsInactive: false,
    isAdmin: false,
    isCountyAdmin: false,
    isAssessor: false,
    isSolicitor: false,
    isCountyClerk: false,
    isAuthorizedAttorney: false,
    isCountyCommissioner: false,
  },
  userPreferencesRestStatus: {
    status: RestStatus.NOT_STARTED,
  },
  userActivityRestStatus: {
    status: RestStatus.NOT_STARTED
  }
};

const cognitoUserChanged = (prevCognitoUser: CognitoUser | undefined, newCognitoUser: CognitoUser | undefined) =>
  newCognitoUser?.username !== prevCognitoUser?.username;

export const setUser = createAsyncThunk(
  'app/setUser',
  async (_: any, { dispatch, requestId }) => {
    const userAndGroups = await getCurrentUserAndGroups();
    if (!userAndGroups) {
      return { user: null, aatResponse: null };
    }
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/userconfig`
    );
    console.log('Get AAT Response', response);
    dispatch(fetchUserPreferences({ source: requestId }));
    if (userAndGroups.userGroups.includes(UserRoles.COUNTY_ADMIN) || userAndGroups.userGroups.includes(UserRoles.ADMIN)) {
      dispatch(downloadStateAppealsForCounty(response.data.user.County));
    }
    return { userAndGroups, aatResponse: response.data as UserConfigurationResponse };
  },
  {
    condition: (user, { getState }) => {
      const state = getState() as RootState;
      const userState = selectUserState(state);
      const theUser = user || getCurrentUserAndGroups();
      if (!theUser || cognitoUserChanged(userState.cognitoUser, theUser.cognitoUser)) {
        return !selectIsUserLoading(state);
      }
      return !selectIsUserLoaded(state);
    },
  }
);
export const fetchUserPreferences = createAsyncThunk(
  'app/fetchUserPreferences',
  //@ts-ignore
  async ({ source }: { source: string }) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/userconfig/contactdefaults`
    );
    return response.data;
  }
);

export const saveUserPreferences = createAsyncThunk(
  'app/saveUserPreferences',
  async (
    { contact, additionalContact, solicitorContact }: { contact: UserContactDefault, additionalContact: AdditionalContact, solicitorContact: SolicitorContact },
    { dispatch, requestId }
  ) => {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/userconfig/contactdefaults`,
      {
        contact: contact,
        additionalContact: additionalContact,
        solicitorContact: solicitorContact,
      }
    );
    dispatch(fetchUserPreferences({ source: requestId }));
    return response.data as string;
  }
);

export const sendUsernameToEmail = createAsyncThunk(
  'app/sendUsernameToEmail',
  async (email: string) => {
    const response = await axios
      .get(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/userconfig/username`, {
        params: { email }
      });
    return response.data;
  }
);

export const logUserActivity = createAsyncThunk(
  'app/logUserActivity',
  async ({ data, appeal, stage, button }: { data: any, appeal?: NewAppeal, stage: number, button: string }) => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/useractivity`,
      {
        appeal,
        data,
        stage,
        button,
      }
    );
    return response.data;
  }
);

const convertToStandardContact = (
  nonstandardContact: any,
  prefix: string,
  notifyPrefix: string
) => {
  const prefixLength = prefix.length;
  const standardContact: { [index: string]: any } = {};
  return Object.entries(nonstandardContact).reduce((contact, [attribute, value]) => {
    let standardAttribute = attribute;
    if (notifyPrefix + 'PhoneCheckbox' === attribute) {
      standardAttribute = 'notifyBySMS';
    } else if (notifyPrefix + 'EmailCheckbox' === attribute) {
      standardAttribute = 'notifyByEmail';
    } else if (prefixLength && attribute.startsWith(prefix)) {
      standardAttribute =
        attribute.substring(prefixLength, prefixLength + 1).toLowerCase() +
        attribute.substring(prefixLength + 1);
    }
    contact[standardAttribute as keyof typeof Object] = value;
    return contact;
  }, standardContact);
};

export const appSlice = createSlice({
  name: 'app',
  initialState: initialState,
  reducers: {
    _setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUpdatingContent: (state, action: PayloadAction<boolean>) => {
      state.isUpdatingContent = action.payload;
    },
    setLayoutState: (state, action: PayloadAction<Partial<LayoutState>>) => {
      const layoutState = { ...state.layoutState, ...action.payload };
      window.localStorage.setItem('app.layoutState', JSON.stringify(layoutState));
      state.layoutState = layoutState;
    },
    toggleMobileMenu: (state) => {
      state.layoutState.menuMobileOpened = !state.layoutState.menuMobileOpened;
    },
    closeMobileMenu: (state) => {
      state.layoutState.menuMobileOpened = false;
    },
    setPathName: (state, action: PayloadAction<string>) => {
      state.contentBuffer.pathName = action.payload;
    },
    setMenuData: (state, action: PayloadAction<MenuEntry[]>) => {
      state.menuData = action.payload;
    },
    setForceRefresh: (state) => {
      state.layoutState.refreshNeeded = true;
    },
    clearUser: (state) => {
      state.userState = initialState.userState;
      state.userPreferences = undefined;
      state.userPreferencesRestStatus = { status: RestStatus.NOT_STARTED };
    },
    setNetworkError: (state, action: PayloadAction<string>) => {
      state.networkError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUser.pending, (state) => {
        state.userState.aatLoading = true;
        state.userState.aatError = '';
      })
      .addCase(setUser.fulfilled, (state, action) => {
        const { userAndGroups, aatResponse } = action.payload;
        if (userAndGroups) {
          const { userGroups } = userAndGroups;
          const responseUser = aatResponse?.user || {};
          state.userState = {
            ...userAndGroups,
            user: responseUser,
            county: responseUser.County,
            countyIsInactive: responseUser.County !== undefined && !CountyDefinitions.some(c => c.key === responseUser.County?.key && c.active),
            municipalities: responseUser.Municipalities,
            aatLoaded: true,
            aatLoading: false,
            isCountyAdmin: userGroups.includes(UserRoles.COUNTY_ADMIN),
            isCountyClerk: userGroups.includes(UserRoles.COUNTY_CLERK),
            isCountyCommissioner: userGroups.includes(UserRoles.COUNTY_COMMISSIONER),
            isAssessor: userGroups.includes(UserRoles.ASSESSOR),
            isSolicitor: userGroups.includes(UserRoles.SOLICITOR),
            isAdmin: userGroups.includes(UserRoles.ADMIN),
            isAuthorizedAttorney: userGroups.includes(UserRoles.AUTHORIZED_ATTORNEY),
          };
        } else {
          state.userState = initialState.userState;
        }
      })
      .addCase(setUser.rejected, (state, action) => {
        state.userState.aatLoaded = false;
        state.userState.aatLoading = false;
        state.userState.aatError = action.error.message;
      })
      .addCase(fetchUserPreferences.pending, (state, action) => {
        state.userPreferences = undefined;
        state.userPreferencesRestStatus = {
          status: RestStatus.LOADING,
          source: action.meta.arg.source,
          requestId: action.meta.requestId,
        };
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        const preferences = action.payload;
        const { contactInfo } = preferences;
        if (contactInfo) {
          const { contact } = contactInfo;
          if (Object.hasOwn(contact, 'mainEmailCheckbox')) {
            contactInfo.contact = convertToStandardContact(contact, '', 'main');
          }
          const { additionalContact } = contactInfo;
          if (Object.hasOwn(additionalContact, 'additionalFirstName')) {
            contactInfo.additionalContact = convertToStandardContact(
              additionalContact,
              'additional',
              'contact'
            );
          }
          const { solicitorContact } = contactInfo;
          contactInfo.solicitorContact = solicitorContact || { isActive: false };
        }
        state.userPreferences = preferences;
        state.userPreferencesRestStatus.status = RestStatus.LOADED;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.userPreferencesRestStatus.status = RestStatus.LOAD_FAILED;
        state.userPreferencesRestStatus.error = action.error.message;
      })
      .addCase(saveUserPreferences.pending, (state, action) => {
        state.userPreferencesRestStatus = {
          status: RestStatus.SAVING,
          requestId: action.meta.requestId,
        };
      })
      .addCase(saveUserPreferences.fulfilled, (state) => {
        state.userPreferencesRestStatus.status = RestStatus.SAVED;
      })
      .addCase(saveUserPreferences.rejected, (state, action) => {
        state.userPreferencesRestStatus.status = RestStatus.SAVE_FAILED;
        state.userPreferencesRestStatus.error = action.error.message;
      })
      .addCase(logUserActivity.pending, (state, action) => {
        state.userActivityRestStatus = {
          status: RestStatus.SAVING,
          requestId: action.meta.requestId,
        };
      })
      .addCase(logUserActivity.fulfilled, (state) => {
        state.userActivityRestStatus.status = RestStatus.SAVED;
      })
      .addCase(logUserActivity.rejected, (state, action) => {
        state.userActivityRestStatus.status = RestStatus.SAVE_FAILED;
        state.userActivityRestStatus.error = action.error.message;
        state.networkError = 'Log user activity failed: ' + action.error.message;
      })
      .addCase(sendUsernameToEmail.pending, (state) => {
        state.reminderRestStatus = { status: RestStatus.LOADING };
      })
      .addCase(sendUsernameToEmail.fulfilled, (state, action) => {
        state.reminderRestStatus = { status: RestStatus.LOADED };
        state.numberOfUsernamesFound = action.payload.count;
      })
      .addCase(sendUsernameToEmail.rejected, (state, action) => {
        state.reminderRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
  }
});

export const selectUserState = (state: RootState) => state.app.userState;
export const selectUserCounty = (state: RootState) => state.app.userState.county;
export const selectUserMunicipalities = (state: RootState) => state.app.userState.municipalities;
export const selectIsUserLoading = (state: RootState) => state.app.userState.aatLoading;
export const selectIsUserLoaded = (state: RootState) => state.app.userState.aatLoaded;
export const selectIsCountyInactive = (state: RootState) => state.app.userState.countyIsInactive;
export const selectUser = (state: RootState) => state.app.userState.user;
export const selectUserName = (state: RootState) => state.app.userState.cognitoUser?.username;
export const selectUserGroups = (state: RootState) => state.app.userState.userGroups || [];
export const selectUserIsAdmin = (state: RootState) => state.app.userState.isAdmin;
export const selectUserIsAssessor = (state: RootState) => state.app.userState.isAssessor;
export const selectUserIsAuthorizedAttorney = (state: RootState) =>
  state.app.userState.isAuthorizedAttorney;
export const selectUserIsCountyAdmin = (state: RootState) => state.app.userState.isCountyAdmin;
export const selectUserIsCountyClerk = (state: RootState) => state.app.userState.isCountyClerk;
export const selectUserIsCountyCommissioner = (state: RootState) =>
  state.app.userState.isCountyCommissioner;
export const selectUserAATError = (state: RootState) => state.app.userState.aatError;
export const selectUserIsSolicitor = (state: RootState) => state.app.userState.isSolicitor;
export const selectUserIsAppellant = (state: RootState) => !state.app.userState?.userGroups?.length;
export const selectCognitoUserEmail = (state: RootState) => state.app.userState.cognitoUser?.email;
export const selectCognitoUserPhone = (state: RootState) => state.app.userState.cognitoUser?.phoneNumber;
export const selectUserPreferences = (state: RootState) => state.app.userPreferences;
export const selectUserPreferencesRestStatus = (state: RootState) =>
  state.app.userPreferencesRestStatus;
export const selectUserPreferencesError = (state: RootState) => state.app.userPreferencesRestStatus.error;
export const selectMenuData = (state: RootState) => state.app.menuData;
export const selectPathName = (state: RootState) => state.app.contentBuffer.pathName;
export const selectNetworkError = (state: RootState) => state.app.networkError;
export const selectLayoutState = (state: RootState) => state.app.layoutState;
export const selectRefreshNeeded = (state: RootState) => state.app.layoutState.refreshNeeded;
export const {
  setUpdatingContent,
  setLayoutState,
  setForceRefresh,
  setPathName,
  setMenuData,
  _setLoading,
  clearUser,
  setNetworkError,
  toggleMobileMenu,
  closeMobileMenu,
} = appSlice.actions;

export default appSlice.reducer;